import React, { useEffect } from 'react';

import { useSelector } from 'react-redux';

import Button from 'components/Button';
import { CurrentlyPlaying } from 'components/ConnectedAudioPlayer/hls-helper';
import IconFemtasy from 'components/IconFemtasy';
import Image from 'components/Image';
import PlayButtonWrapper from 'components/PlayButtonWrapper';

import {
  StoryActions,
  StoryDetailsFragment,
  useGetStoryForPlayerLazyQuery,
} from 'graphql/generated/graphql';

import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import { usePlayer } from 'hooks/usePlayer';
import { useQueryParams } from 'hooks/useQueryParams';
import { useQueue } from 'hooks/useQueue';

import { getSource } from 'store/modules/algolia_tracking_context';
import ReduxState from 'store/ReduxState';

import { useUrls } from 'constants/urls';
import { ContentType } from 'types/contentType';

import openLockImage from './images/openLock.svg';

import './style.scss';

interface IProps {
  story: StoryDetailsFragment;
  audioUrl?: string;
  includeAlgoliaConversion?: boolean;
}

export const usePlaybackControls = ({
  story,
  audioUrl,
  includeAlgoliaConversion,
}: IProps) => {
  const algoliaSource = useSelector(getSource);
  const { initQueueWithStory } = useQueue();

  const { playStoryAudio, playStoryTeaser, stopPlayback } = usePlayer();

  const playTeaser = React.useCallback(() => {
    if (!story.transcodedSampleUrl) return;
    playStoryTeaser({
      id: story.id,
      audioUrl: story.transcodedSampleUrl,
      storyTitle: story.title,
      speakerName: story.speaker.name,
    });
  }, [playStoryTeaser, story]);

  const playAudio = React.useCallback(() => {
    stopPlayback();
    playStoryAudio({
      id: story.id,
      storyTitle: story.title,
      speakerName: story.speaker.name,
      audioUrl,
      algoliaSource: includeAlgoliaConversion ? algoliaSource : undefined,
      orgasms: story.orgasms,
      contentType: story.contentType.slug as ContentType,
    });
    initQueueWithStory(story);
  }, [
    story,
    stopPlayback,
    playStoryAudio,
    audioUrl,
    includeAlgoliaConversion,
    algoliaSource,
    initQueueWithStory,
  ]);

  return { playAudio, playTeaser };
};

const Buttons = ({ story }: IProps) => {
  const { pricingPage } = useUrls();

  const [getStory, { data }] = useGetStoryForPlayerLazyQuery({
    ...useGraphaelDefaults(),
    fetchPolicy: 'no-cache',
  });
  const queryParams = useQueryParams();

  const { playAudio, playTeaser } = usePlaybackControls({
    story,
    audioUrl: data?.story?.transcodedAudioUrl || undefined,
    includeAlgoliaConversion:
      (queryParams.get('source') as string) === 'search' || false,
  });

  const currentlyPlaying = useSelector<
    ReduxState,
    CurrentlyPlaying | undefined
  >(({ player }) => player.currentlyPlaying);

  useEffect(() => {
    if (
      (!currentlyPlaying || currentlyPlaying.audioType !== 'audio') &&
      !data
    ) {
      getStory({ variables: { storyId: story.id } });
    }
  }, [currentlyPlaying, data, getStory, story.id]);

  return (
    <div className="buttons">
      {story.actions.includes(StoryActions.Play) ? (
        <PlayButtonWrapper story={story}>
          <Button
            title={{ id: 'components.StoryDetails.play' }}
            className="buttons__playAudio"
            onClick={playAudio}
            variant="primary"
            icon={{
              component: <IconFemtasy icon="Playsolid" color="white" />,
            }}
          />
        </PlayButtonWrapper>
      ) : (
        <>
          <Button
            linkTo={pricingPage}
            title={{ id: 'components.StoryDetails.unlock' }}
            icon={{
              component: (
                <Image
                  src={openLockImage}
                  alt={{ id: 'components.Buttons.openLock.alt' }}
                />
              ),
            }}
          />
          {story.transcodedSampleUrl && (
            <Button
              title={{ id: 'components.StoryDetails.playTeaser' }}
              className="playTeaser"
              onClick={playTeaser}
              variant="secondary"
            />
          )}
        </>
      )}
    </div>
  );
};

export default Buttons;
